import React, { useState } from "react";
import { Mail, Lock, Loader2, Sparkles, Eye, EyeOff, ArrowLeft, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ChangePasswordForm({ onBack, initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !current || !next) {
      setError("Please fill in every field.");
      return;
    }
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (next === current) {
      setError("New password must be different from the current one.");
      return;
    }
    setBusy(true);
    try {
      await endpoints.authChangePassword({
        email: email.trim(),
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Please sign in with your new password.");
      onBack?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <div className="text-center mb-2">
        <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 px-3 py-1 text-[10px] font-semibold tracking-widest uppercase text-indigo-700 dark:text-indigo-300">
          <KeyRound className="h-3 w-3" /> Change password
        </div>
        <h1 className="font-display font-extrabold text-2xl tracking-tight mt-3">Set a new password</h1>
        <p className="text-sm text-slate-500 mt-1">Enter your current password and the new one you'd like to use.</p>
      </div>

      <div>
        <Label htmlFor="cp-email" className="text-xs">Email</Label>
        <div className="relative mt-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input id="cp-email" data-testid="cp-email" type="email" value={email}
            onChange={e => setEmail(e.target.value)} placeholder="you@company.com"
            className="pl-9" autoComplete="email" autoFocus required />
        </div>
      </div>

      <div>
        <Label htmlFor="cp-current" className="text-xs">Current password</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input id="cp-current" data-testid="cp-current" type={showPwd ? "text" : "password"} value={current}
            onChange={e => setCurrent(e.target.value)} placeholder="••••••••"
            className="pl-9 pr-10" autoComplete="current-password" required />
          <button type="button" onClick={() => setShowPwd(s => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            tabIndex={-1} aria-label={showPwd ? "Hide passwords" : "Show passwords"}>
            {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div>
        <Label htmlFor="cp-new" className="text-xs">New password</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input id="cp-new" data-testid="cp-new" type={showPwd ? "text" : "password"} value={next}
            onChange={e => setNext(e.target.value)} placeholder="At least 8 characters"
            className="pl-9" autoComplete="new-password" required minLength={8} />
        </div>
      </div>

      <div>
        <Label htmlFor="cp-confirm" className="text-xs">Confirm new password</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input id="cp-confirm" data-testid="cp-confirm" type={showPwd ? "text" : "password"} value={confirm}
            onChange={e => setConfirm(e.target.value)} placeholder="Repeat the new password"
            className="pl-9" autoComplete="new-password" required minLength={8} />
        </div>
      </div>

      {error && (
        <div data-testid="cp-error" className="rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-xs px-3 py-2">
          {error}
        </div>
      )}

      <Button type="submit" disabled={busy}
        className="w-full brand-gradient text-white font-semibold shadow-lg shadow-indigo-500/25"
        data-testid="cp-submit">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
        {busy ? "Updating…" : "Update password"}
      </Button>

      <button type="button" onClick={onBack} data-testid="cp-back"
        className="w-full text-xs text-slate-500 hover:text-indigo-600 flex items-center justify-center gap-1 pt-1">
        <ArrowLeft className="h-3 w-3" /> Back to sign in
      </button>
    </form>
  );
}
